//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import axios from 'axios';

import {
  AGENT_CONTRACT_VERSION, type ApprovalDecision, type ConversationView,
  isConversationView, type PageSnapshot
} from '@/agent/contract';
import { type ApiClient } from './apiClient';

// The backend refused the contract version this build speaks.  Distinct
// from every other failure because the remedy is different: reload, not
// retry.  Never downgraded or reshaped here; see the spec's "Version".
export class ContractVersionError extends Error {
  constructor(readonly accepted: number[]) {
    super('The server no longer accepts this build\'s agent contract ' +
          `version ${AGENT_CONTRACT_VERSION}; reload to update.`);
    this.name = 'ContractVersionError';
  }
}

// The backend has no such conversation: it expired under the store's TTL,
// the store was replaced, or the id was minted against another deployment.
// Distinct from every other failure because the remedy is the same as a
// version refusal's -- forget the id and start clean -- and not a retry,
// which would fail identically for as long as the id is kept.
export class ConversationGoneError extends Error {
  constructor() {
    super('That conversation is no longer on the server.');
    this.name = 'ConversationGoneError';
  }
}

// The conversation did not record the permission profile it started under,
// having begun before permission control, so no turn can be held to it. A
// changed profile is not refused; its turns take the more restrictive of the
// two. Distinct from every other failure for the same reason as the two
// above: the remedy is to forget the id and start clean, not to retry, which
// would fail identically forever.
export class ProfileNotRecordedError extends Error {
  constructor() {
    super('This conversation started before its data permissions were ' +
          'recorded.');
    this.name = 'ProfileNotRecordedError';
  }
}

const VERSION_REFUSAL = 'unsupported agent contract version';
const PROFILE_REFUSAL = 'permission profile not recorded';

const rethrowRefusal = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 409) {
    const body: unknown = error.response.data;
    if (body !== null && typeof body === 'object' &&
        (body as { error?: unknown }).error === PROFILE_REFUSAL)
      throw new ProfileNotRecordedError();
    if (body !== null && typeof body === 'object' &&
        (body as { error?: unknown }).error === VERSION_REFUSAL) {
      const accepted = (body as { accepted?: unknown }).accepted;
      throw new ContractVersionError(
        Array.isArray(accepted) ?
          accepted.filter((v): v is number => typeof v === 'number') : []);
    }
  }
  throw error;
};

// For the calls that name a conversation in their path.  A 404 from the
// create route would mean the route itself is missing, which is not this.
const rethrowConversationRefusal = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 404)
    throw new ConversationGoneError();
  return rethrowRefusal(error);
};

// A turn/approval answers with the async id as a bare text body.  The
// backend mints it as an integer and sends its decimal text, which axios
// JSON-parses back into a number before the service sees it (0 included:
// it is the first id a fresh session hands out).  A 200 with an empty
// body, though, resolves to undefined regardless of makeRequest's declared
// Promise<T>, and coercing that would mint the literal "undefined" -- a
// plausible-looking id that then fails far from here, against the
// backend, once polled.  Guard it here instead, once, for both callers.
const requireAsyncId = (value: unknown, label: string): string => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0)
    return String(value);
  if (typeof value !== 'string' || value === '')
    throw new Error(`${label} answered without an async id`);
  return value;
};

/**
 * The agent runtime's endpoints: conversations, turns, and approvals.  A
 * turn or approval answers with an async id that the existing asyncStatus
 * polling drives to a TurnResult.
 */
export class AgentService {
  constructor(private readonly api: ApiClient) {}

  createConversation = async (): Promise<string> => {
    try {
      const response = await this.api.makeRequest<{ conversation_id: string }>(
        'agent/conversations', 'post',
        { contract_version: AGENT_CONTRACT_VERSION });
      if (typeof response?.conversation_id !== 'string')
        throw new Error('Conversation create answered without an id');
      return response.conversation_id;
    } catch (error) {
      return rethrowRefusal(error);
    }
  };

  getConversation = async (id: string): Promise<ConversationView> => {
    try {
      const response = await this.api.makeRequest<unknown>(
        `agent/conversations/${encodeURIComponent(id)}`, 'get');
      if (!isConversationView(response))
        throw new Error('Conversation read was not the contract shape');
      return response;
    } catch (error) {
      return rethrowConversationRefusal(error);
    }
  };

  sendTurn = async (
    id: string, message: string, snapshot: PageSnapshot, llm: string
  ): Promise<string> => {
    try {
      const asyncId = await this.api.makeRequest<string>(
        `agent/conversations/${encodeURIComponent(id)}/turns`, 'post',
        { message, snapshot, llm, contract_version: AGENT_CONTRACT_VERSION });
      return requireAsyncId(asyncId, 'Turn send');
    } catch (error) {
      return rethrowConversationRefusal(error);
    }
  };

  sendApproval = async (
    id: string, approvalId: string, decision: ApprovalDecision
  ): Promise<string> => {
    try {
      const asyncId = await this.api.makeRequest<string>(
        `agent/conversations/${encodeURIComponent(id)}/approvals`, 'post',
        { approval_id: approvalId, decision,
          contract_version: AGENT_CONTRACT_VERSION });
      return requireAsyncId(asyncId, 'Approval send');
    } catch (error) {
      return rethrowConversationRefusal(error);
    }
  };
}
